import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common, query } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { GlobalDisclaimerLevel } from '@kurtosys/types/disclaimers/GlobalDisclaimerLevel.js';
import { ITargettedDisclaimers } from '@kurtosys/types/disclaimers/ITargettedDisclaimers.js';
import { observer } from 'mobx-react';
import { Label, Legend, RadialBar, RadialBarChart, ResponsiveContainer, Tooltip } from 'recharts';

import { IChartData, IRadialBarProps, TChartElement } from '../../../models/charting/index.js';
import { InjectedStyledComponent, QueryStore, ResponsiveComponentBase } from '../../../shared/index.js';
import { ITableProps } from '../../base/Table/models/ITableProps.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';
import { applyFormatters, applyTranslationsToDataName } from '../ChartBase/ChartBaseFunctions.js';
import DefaultTooltipContent from '../ChartBase/components/DefaultTooltipContent/DefaultTooltipContent.js';

import { IRadialChartConfiguration, IRadialChartProps, IRadialChartState } from './models/index.js';
import { IRadialChartElement } from './models/IRadialChartElement.js';
import DisclaimersWrapper from './styledComponents/DisclaimersWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { RADIAL_CHART_CATEGORY_KEY, RADIAL_CHART_COMPONENT_KEY } from './RadialChart.meta.js';

export class RadialChart extends ResponsiveComponentBase<
	IRadialChartProps,
	IRadialChartState,
	IRadialChartConfiguration
> {
	categoryKey = RADIAL_CHART_CATEGORY_KEY;
	componentKey = RADIAL_CHART_COMPONENT_KEY;
	tableFormatter: Partial<IFormatter> = {};

	constructor(props: IRadialChartProps) {
		super(props);
	}

	getFormatter(element: IRadialChartElement, type: 'value' | 'label'): (value: any) => any {
		if (type === 'value') {
			if (element.props && element.props.formatter) {
				return element.props.formatter as (value: any) => any;
			}
		}
		if (type === 'label') {
			if (element.props && element.props.labelFormatter) {
				return element.props.labelFormatter as (value: any) => any;
			}
		}
		return (value) => value;
	}

	applyLabelFormatting = (element: IRadialChartElement) => {
		const { props } = element;
		if (element.type === 'RadialBar' && props) {
			if (typeof props.label === 'object') {
				const { showLabel = false, showValue = true } = props.label;
				const valueFormatter = this.getFormatter(element, 'value');
				const labelFormatter = this.getFormatter(element, 'label');
				props.label.content = (labelProps: any) => {
					const { content, ...otherProps } = labelProps;
					return (
						<Label {...otherProps}>
							{`${showLabel ? labelFormatter(labelProps.name) : ''} ${
								showValue ? valueFormatter(labelProps.value) : ''
							}`}
						</Label>
					);
				};
			} else if (typeof props.label === 'boolean' && props.label) {
				const valueFormatter = this.getFormatter(element, 'value');
				props.label = (labelProps: any) => {
					const { content, ...otherProps } = labelProps;
					return <Label {...otherProps}>{valueFormatter(labelProps.value)}</Label>;
				};
			}
		}
	};

	radialBarComponent = (props: IRadialChartProps, translationContext: ITranslationContextValue) => {
		const { radialBarProps } = props;
		if (radialBarProps) {
			const radialBarElement: IRadialChartElement = {
				type: 'RadialBar',
				props: radialBarProps,
			};
			applyFormatters(radialBarElement, translationContext);
			this.applyLabelFormatting(radialBarElement);
			const { props: radialBarElementProps } = radialBarElement;

			return <RadialBar {...(radialBarElementProps as IRadialBarProps)} />;
		}
	};

	applyGlobalDisclaimers = (
		disclaimers: ITargettedDisclaimers[],
		data: IChartData[] = [],
		queryStore?: QueryStore | undefined,
		appKey?: string | undefined,
	) => {
		let hasSuperscriptDisclaimer = false;
		const globalDisclaimers = new Map<string, any[]>();
		if (disclaimers !== undefined && queryStore && appKey) {
			query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
			const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
			if (globalDisclaimerStore) {
				hasSuperscriptDisclaimer = true;

				for (const targettedDisclaimer of disclaimers) {
					const { disclaimers: disclaimersConfig = [], conditional } = targettedDisclaimer;
					if (conditional) {
						// TODO: need to figure out the first arg
						const conditionalHelper = new common.helpers.ConditionalHelper({}, conditional);
						if (
							conditionalHelper.matchesWithOptions({
								instance: data,
								executionOptions: { responseContext: data },
							})
						) {
							const workingDisclaimers = globalDisclaimerStore.add(
								appKey,
								data,
								queryStore,
								...disclaimersConfig,
							);
							// pushes the current element disclaimers onto the props so that we can get the relevant superscripts
							globalDisclaimers.set((this.props.radialBarProps as any).dataKey, workingDisclaimers);
						}
					} else {
						const workingDisclaimers = globalDisclaimerStore.add(
							appKey,
							data,
							queryStore,
							...disclaimersConfig,
						);
						// pushes the current element disclaimers onto the props so that we can get the relevant superscripts
						globalDisclaimers.set((this.props.radialBarProps as any).dataKey, workingDisclaimers);
					}
				}
			}
		}
		return { hasSuperscriptDisclaimer, globalDisclaimers };
	};

	renderRadialBarChart = (props?: IRadialChartProps) => {
		if (!props) {
			return null;
		}
		const {
			// Fix build issue. bad typings
			responsiveContainerProps = {} as any,
			showTooltip = true,
			showLegend = true,
			legend,
			data,
			tooltip,
			appKey,
			queryStore,
			radialBarProps,
			colors = [],
		} = props;
		let { disclaimersProps } = props;

		let _globalDisclaimers: Map<string, any[]>;
		// appKey is required so that we don't get all disclaimers returning when they are not setup
		if (appKey && radialBarProps) {
			const { globalDisclaimers } = this.applyGlobalDisclaimers(
				radialBarProps.disclaimers || [],
				data,
				queryStore,
				appKey,
			);
			query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
			const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
			if (globalDisclaimerStore) {
				const disclaimers = [];
				// gets all disclaimers to show at the bottom of the chart
				disclaimers.push(...(globalDisclaimerStore.get(GlobalDisclaimerLevel.app, appKey) as any[]));
				if (disclaimers && disclaimers.length > 0) {
					disclaimersProps = disclaimersProps || {};
					disclaimersProps.disclaimers = disclaimersProps.disclaimers || [];
					disclaimersProps.disclaimers.push(...disclaimers);
				}
			}
			_globalDisclaimers = globalDisclaimers;
		}

		return (
			<Fragment>
				<TranslationContext.Consumer>
					{(translationContext) => {
						applyTranslationsToDataName(translationContext, data);
						const tooltipElement: TChartElement = { type: 'Tooltip' };
						if (showTooltip) {
							tooltipElement.props = tooltip;
							if (_globalDisclaimers) {
								(tooltipElement.props as any)._globalDisclaimers = _globalDisclaimers;
							}
							applyFormatters(tooltipElement, translationContext);
						}
						let workingData = data;
						if (colors.length > 0) {
							workingData = data?.map((item, idx) => {
								return {
									...item,
									fill: colors[idx % colors.length],
								};
							});
						}
						return (
							<div aria-hidden={true}>
								<ResponsiveContainer minHeight="300px" {...responsiveContainerProps}>
									<RadialBarChart {...props} data={workingData}>
										{this.radialBarComponent(props, translationContext)}
										{showTooltip && (
											<Tooltip
												{...(tooltipElement.props as any)}
												content={<DefaultTooltipContent usePayloadName={true} />}
											/>
										)}
										{showLegend && <Legend {...(legend as any)} />}
									</RadialBarChart>
								</ResponsiveContainer>
							</div>
						);
					}}
				</TranslationContext.Consumer>
				{disclaimersProps && (
					<DisclaimersWrapper>
						<Disclaimers {...disclaimersProps} />
					</DisclaimersWrapper>
				)}
			</Fragment>
		);
	};

	getTableProps(props?: IRadialBarProps): ITableProps {
		if (props) {
			const dataKey = props.dataKey.toString();
			if (common.commonUtils.isNullOrEmpty(this.tableFormatter)) {
				// Due to the formatter being updated in ChartBaseFunctions.tsx line 387, we need to store the format config for the table
				this.tableFormatter = props.formatter && typeof props.formatter !== 'function' ? props.formatter : {};
			}
			return {
				columns: [
					{
						id: 'name',
						key: 'name',
						header: '',
					},
					{
						id: dataKey,
						key: dataKey,
						header: 'Value',
						format: this.tableFormatter,
					},
				],
				rows: props.data,
			};
		}
		return {};
	}

	render() {
		const { className } = this.props;
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
					return (
						<Breakpoint {...breakpointProps}>
							<Wrapper className={className}>{this.renderRadialBarChart(props)}</Wrapper>
						</Breakpoint>
					);
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(RadialChart), RADIAL_CHART_CATEGORY_KEY, RADIAL_CHART_COMPONENT_KEY);
